(function (global) {
  'use strict';

  // Shared Brooklyn Bedding cart / section helpers.
  // Load before bb-cart.js, bb-product-recommendations.js, etc.
  if (global.BB) return;

  // ---------------------------------------------------------------------------
  // Roots / routes
  // ---------------------------------------------------------------------------

  // Return the locale-aware Shopify routes root, or "/".
  function shop_root() {
    // Prefer Shopify's locale-aware root when available.
    if (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) {
      return global.Shopify.routes.root;
    }

    return '/';
  }

  // Return the cart page root element.
  function cart_page() {
    return document.querySelector('[data-bb-cart]');
  }

  // Collect section IDs that should refresh after cart changes.
  // Pass extra IDs (product page sections, etc.) as an array.
  function section_ids(extra_ids) {
    const ids = [];
    const root = cart_page();

    // Include the cart section when data-section-id is present.
    if (root && root.getAttribute('data-section-id')) {
      ids.push(root.getAttribute('data-section-id'));
    }

    (extra_ids || []).forEach(function (id) {
      // Skip blanks and duplicates.
      if (!id || ids.indexOf(id) !== -1) return;
      ids.push(id);
    });

    return ids;
  }

  // Build the sections payload shared by cart Ajax mutations.
  function sections_payload(ids) {
    const section_list = ids || section_ids();

    // No sections to request when nothing was collected.
    if (!section_list.length) return {};

    return {
      sections: section_list.join(','),
      sections_url: window.location.pathname,
    };
  }

  // Toggle the cart loading state on the page root.
  function set_cart_loading(is_loading) {
    const root = cart_page();
    // Exit when the cart page is not mounted.
    if (!root) return;

    root.classList.toggle('is-loading', is_loading);
  }

  // ---------------------------------------------------------------------------
  // Fetch / POST
  // ---------------------------------------------------------------------------

  // Parse Cart Ajax / section responses for the bb-cart stack.
  function read_cart_json_response(response) {
    return response.text().then(function (text) {
      const trimmed = (text || '').trim();

      if (!trimmed) {
        // Empty body on a failed response — treat as a cart API error.
        if (!response.ok) throw new Error('Cart request failed. Please try again.');
        return {};
      }

      try {
        return JSON.parse(trimmed);
      } catch (error) {
        // Safari throws opaque errors from response.json() on HTML bodies.
        throw new Error('Cart request failed. Please try again.');
      }
    });
  }

  // POST JSON to a Cart Ajax API endpoint and reject cart errors.
  function post_cart(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(read_cart_json_response)
      .then(function (data) {
        // Surface Shopify cart API failures as thrown errors.
        if (data.status || data.errors || data.message === 'Cart Error') {
          throw new Error(data.description || data.message || data.errors || 'Cart update failed');
        }

        return data;
      });
  }

  // Fetch the current cart via Cart Ajax API.
  function fetch_cart() {
    return fetch(shop_root() + 'cart.js').then(read_cart_json_response);
  }

  // Fetch rendered section HTML via the Section Rendering API.
  function fetch_sections(ids) {
    // Nothing to fetch when no section IDs were provided.
    if (!ids || !ids.length) return Promise.resolve({});

    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));

    return fetch(url.toString()).then(function (response) {
      // Fail loudly when section rendering returns a non-OK response.
      if (!response.ok) throw new Error('Unable to refresh sections');
      return read_cart_json_response(response);
    });
  }

  // ---------------------------------------------------------------------------
  // Section rendering
  // ---------------------------------------------------------------------------

  // Replace a Shopify section node with freshly rendered HTML.
  function replace_section(section_id, html) {
    // Exit when required inputs are missing.
    if (!section_id || !html) return;

    const current_section = document.getElementById('shopify-section-' + section_id);
    // Exit when the live section is not on the page.
    if (!current_section) return;

    const document_fragment = new DOMParser().parseFromString(html, 'text/html');
    const next_section = document_fragment.getElementById('shopify-section-' + section_id);
    // Exit when the response HTML does not include this section.
    if (!next_section) return;

    current_section.replaceWith(next_section);
  }

  // Apply section HTML from a cart response, or fetch sections if none were returned.
  function apply_sections(sections_html, fallback_ids) {
    const ids = sections_html ? Object.keys(sections_html) : fallback_ids || section_ids();

    // Prefer bundled section HTML from the cart API response.
    if (sections_html && ids.length) {
      ids.forEach(function (section_id) {
        // Skip empty/missing section payloads.
        if (sections_html[section_id]) replace_section(section_id, sections_html[section_id]);
      });
      return Promise.resolve(sections_html);
    }

    // Fallback: request sections directly when the cart response had none.
    return fetch_sections(fallback_ids || section_ids()).then(function (sections) {
      Object.keys(sections).forEach(function (section_id) {
        replace_section(section_id, sections[section_id]);
      });
      return sections;
    });
  }

  // ---------------------------------------------------------------------------
  // Cart events / refresh
  // ---------------------------------------------------------------------------

  // Notify the rest of the theme that the cart changed (counts, drawer, etc.).
  function dispatch_cart_update(cart, sections, source) {
    const source_id = source || 'bb-core';

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          sourceId: source_id,
          data: {
            source: source_id,
            itemCount: cart ? cart.item_count : undefined,
            sections: sections || {},
          },
        },
      })
    );
  }

  // Refresh section HTML and broadcast cart:update after a successful cart mutation.
  function refresh_after_cart_change(result, options) {
    const opts = options || {};
    const sections = (result && result.sections) || {};
    const ids = opts.section_ids || section_ids();
    const source = opts.source || 'bb-core';

    // Reuse response sections when present; otherwise fetch them.
    const sections_promise = Object.keys(sections).length
      ? Promise.resolve(sections)
      : fetch_sections(ids);

    return sections_promise.then(function (resolved_sections) {
      apply_sections(resolved_sections, ids);

      // Mutation response already includes a full cart — skip cart.js.
      if (result && typeof result.item_count === 'number') {
        dispatch_cart_update(result, resolved_sections, source);
        return result;
      }

      return fetch_cart().then(function (cart) {
        dispatch_cart_update(cart, resolved_sections, source);
        return cart;
      });
    });
  }

  // Add one or more line items, then refresh sections + fire cart:update.
  function add_items(items, options) {
    const opts = options || {};
    const ids = opts.section_ids || section_ids();
    const source = opts.source || 'bb-core';
    const payload = sections_payload(ids);

    return post_cart(shop_root() + 'cart/add.js', Object.assign({ items: items }, payload)).then(
      function (result) {
        // Caller wants the raw add response only.
        if (opts.skip_refresh) return result;
        return refresh_after_cart_change(result, { source: source, section_ids: ids });
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Misc helpers
  // ---------------------------------------------------------------------------

  // Safely parse JSON from a script/text node.
  function parse_json(node) {
    // Exit when the node is missing.
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  // Format cents using an optional money format, with a simple fallback.
  function format_money(cents, money_format) {
    const amount = Number(cents || 0);

    // Use Shopify.formatMoney when the theme provides it.
    if (money_format && global.Shopify && typeof global.Shopify.formatMoney === 'function') {
      return global.Shopify.formatMoney(amount, money_format);
    }

    return '$' + (amount / 100).toFixed(2);
  }

  global.BB = {
    shop_root: shop_root,
    cart_page: cart_page,
    section_ids: section_ids,
    sections_payload: sections_payload,
    set_cart_loading: set_cart_loading,
    post_cart: post_cart,
    fetch_cart: fetch_cart,
    fetch_sections: fetch_sections,
    replace_section: replace_section,
    apply_sections: apply_sections,
    dispatch_cart_update: dispatch_cart_update,
    refresh_after_cart_change: refresh_after_cart_change,
    add_items: add_items,
    parse_json: parse_json,
    format_money: format_money,
  };
})(window);
